/**
 * Privacy Collection Notice — DRAFT for lawyer review. Client-facing text only.
 *
 * APP 5 requires that, at or before the time we collect personal information (or as soon as
 * practicable after), we take reasonable steps to make the individual aware of the APP 5.2
 * matters. This is the short-form notice shown at the points of collection — account sign-up,
 * the first screen of the client profile (which is also the first thing a client invited by a
 * firm sees), and partner onboarding — with the full Privacy Policy one click away.
 *
 * REVISED 29 Sep 2026 alongside the Privacy Policy: no internal notes in the client text, service
 * providers described by role and country rather than by name, and the countries stated without
 * a bracketed condition. See the policy's header and docs/legal/README.md.
 *
 * It is a NOTICE, not an agreement: it is not part of the clickwrap acceptance ledger and has no
 * server-side version. It shares the LegalDoc shape so LegalDocModal can render it.
 *
 * APP 5.2 matters and where each is covered:
 *   (a) identity and contact details            — "Who is collecting it"
 *   (b) collection from someone else            — "If someone else entered your details"
 *   (c) collection required/authorised by law   — "What the law requires us to keep"
 *   (d) purposes                                — "Why we collect it"
 *   (e) consequences if not collected           — "If you leave things out"
 *   (f) who we usually disclose to              — "Who sees it"
 *   (g) the policy covers access/correction     — "Your rights"
 *   (h) the policy covers complaints            — "Your rights"
 *   (i)/(j) overseas disclosure and countries   — "Who sees it"
 */
export const COLLECTION_NOTICE = {
  key: 'collection_notice',
  version: '2026-10-15',
  title: 'How we handle your information',
  effectiveDate: '15 October 2026',
  draft: true,
  body: `
## Who is collecting it

AgedCareWise. Privacy contact: privacy@agedcarewise.com.au.

## Why we collect it

To model your options and produce your plans and any Statement of Advice; to run and secure
your account; to work with the professional firm helping you, if there is one; to take payment;
and to meet our legal obligations. We do not sell your information or use it for advertising.

## What we collect

Your contact details, your household and living arrangements, your finances, your family and
estate wishes, and — only with your consent — your care needs and health details. For health we
ask only for a general rating (good, fair or poor) and the outcome of any aged-care assessment,
never specific medical conditions or records. Health details are sensitive information. Your
general health helps us flag strategies that tie money up for many years, and your assessment
lets us model care costs and government support. You can leave them blank. A phone number is
optional. If you upload a financial statement to pre-fill your profile, it is read by the AWise
assistant's AI service to draft the entries for your review; neither we nor the AI service keeps
the document. Cover or leave out any tax file number before uploading.

## If someone else entered your details

A professional firm that invited you, or a person acting for you (such as an attorney or family
member), may have entered some of your details before you first signed in. Everything they
entered is here for you to review and correct.

## If you leave things out

We may not be able to create your account, and any modelling will be incomplete or unavailable.

## Who sees it

Your professional firm, if one invited you. The service providers who help us run AgedCareWise:
email delivery, payment processing and the AWise assistant's AI service. Regulators or courts
where the law requires. These providers are overseas, most likely in the United States and Japan.
Your profile, plans and advice records are stored in Australia.

## What the law requires us to keep

If we give you a Statement of Advice, it and the information it was based on are kept for at
least 7 years under financial-services law, even if you close your account or ask us to delete
your information. If a professional firm advises you, the firm decides how long its records are
kept.

## Your rights

Our Privacy Policy explains how to access and correct your information, request deletion, and
make a complaint to us or to the Office of the Australian Information Commissioner.
`,
};

export default COLLECTION_NOTICE;
